import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserRole } from '@/types/user';
import { Shield, Users, User2, Heart, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const roles: {
  id: UserRole;
  title: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    id: 'admin',
    title: 'Administrator',
    description: 'Manage the entire platform, users, and settings',
    icon: <Shield className="h-8 w-8 text-blue-600" />
  },
  {
    id: 'coach',
    title: 'Coach',
    description: 'Manage teams, track performance, and schedule sessions',
    icon: <Users className="h-8 w-8 text-blue-600" />
  },
  {
    id: 'swimmer',
    title: 'Swimmer',
    description: 'Track your progress, view schedules, and manage payments',
    icon: <User2 className="h-8 w-8 text-blue-600" />
  },
  {
    id: 'parent',
    title: 'Parent',
    description: 'Monitor your child\'s activities and manage payments',
    icon: <Heart className="h-8 w-8 text-blue-600" />
  }
];

const Onboarding = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState<UserRole | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRoleSelect = async (role: UserRole) => {
    try {
      if (!user) return;
      
      setIsUpdating(role);
      setError(null);
      
      await user.update({
        unsafeMetadata: {
          role: role,
          onboardedAt: new Date().toISOString()
        }
      });
      
      // Verify the update was successful
      const updatedRole = user.unsafeMetadata?.role;
      if (updatedRole !== role) {
        throw new Error('Role update failed. Please try again.');
      }
      
      toast.success('Role updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating user role:', error);
      setError(error instanceof Error ? error.message : 'Failed to update role. Please try again.');
      toast.error('Failed to update role. Please try again.');
    } finally {
      setIsUpdating(null);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="ml-2 text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            Welcome to Krystal
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            Please select your role to get started
          </p>
          {error && (
            <p className="mt-2 text-sm text-red-600">
              {error}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {roles.map((role) => (
            <Card
              key={role.id}
              className={`p-6 hover:shadow-lg transition-shadow cursor-pointer ${
                isUpdating === role.id ? 'opacity-50' : ''
              }`}
              onClick={() => !isUpdating && handleRoleSelect(role.id)}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  {role.icon}
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {role.title}
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    {role.description}
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    disabled={isUpdating !== null}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRoleSelect(role.id);
                    }}
                  >
                    {isUpdating === role.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      `Select ${role.title}`
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Onboarding; 